import {COMPANY_KEYS, useCompanyQuery} from "@/api/companyApi";
import Button from "@/components/Common/Button";
import AddressForm from "@/components/Common/forms/AddressForm";
import InputField from "@/components/Common/forms/InputField";
import SocialField from "@/components/Common/forms/SocialField";
import Modal from "@/components/Common/Modal";
import {PhoneInput} from "@/components/ui/PhoneInput";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import BrandLogo from "@/pages/profile/BrandLogo";
import {useComposedCampaignState} from "@/store/campaignForm";
import {useCompanyStore} from "@/store/company";
import {isEditProfileModalOpenAtom} from "@/store/jotaiAtoms";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useSetAtom} from "jotai";
import {isEmpty, some} from "lodash";
import {useEffect, type FC} from "react";
import {useTranslation} from "react-i18next";

const EditProfileModal: FC = () => {
  const {t} = useTranslation(["getting_started", "common"]);
  const errors = useCompanyStore(state => state.errors);
  const validatePersonalInfo = useCompanyStore(state => state.personalInfoValidation);
  const setCompanyValue = useCompanyStore(state => state.setValue);
  const campaign = useComposedCampaignState();
  const company = useCompanyStore(state => state.company);
  const setCompany = useCompanyStore(state => state.setCompany);
  const setError = useCompanyStore(state => state.setError);
  const setEditProfileModalOpen = useSetAtom(isEditProfileModalOpenAtom);
  const queryClient = useQueryClient();

  const {data: companyData} = useCompanyQuery({
    companyId: campaign.misc.companyId!!,
    queryOptions: {
      refetchOnMount: true,
    },
  });

  useEffect(() => {
    if (companyData) {
      setCompany(companyData);
    }
  }, [companyData, setCompany]);

  const {mutate: handleCompanyUpdate, isPending: isCompanyUpdatePending} = useMutation({
    mutationKey: COMPANY_KEYS.mCompany(Number(company.id)),
    mutationFn: () =>
      client.patch(COMPANY_KEYS.mCompany(Number(company.id))[0], {
        company_name: company.company_name,
        address_street: company.address_street,
        address_postal: company.address_postal,
        address_city: company.address_city,
        country: company.country,
        phonenumber: company.phonenumber,
        username: company.username,
        tiktok_username: company.tiktok_username,
        place_id: company.place_id,
        place_description: company.place_description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: COMPANY_KEYS.qCompany(Number(company.id)),
      });
      setEditProfileModalOpen(false);
    },
  });

  return (
    <Modal
      backgroundClassName="max-w-207.5 bg-white"
      className="gap-8"
      customBottomRow={<></>}
      modalTitle={t("edit_profile_information")}
      modalTitleClassName="text-base pb-0"
      shouldHideBottomSeparator
      shouldHideHeaderSeparator
      onClose={() => setEditProfileModalOpen(false)}>
      <div className="flex flex-col items-center gap-5">
        <span className="text-neutral-black font-semibold">{t("your_company_logo")}</span>
        <BrandLogo isCompanyProfile />
      </div>
      <InputField
        className={clsxm(!errors.company_name && "border-neutral-grey-light-dark")}
        error={errors.company_name}
        label={t("company_name")}
        labelClassName="text-base text-neutral-black font-semibold"
        onChange={value => setCompanyValue("company_name", value)}
        placeholder={t("company_name_placeholder")}
        value={company.company_name ?? ""}
      />
      <div className="flex flex-col gap-2.5">
        <span className="text-neutral-black font-semibold">{t("add_the_companies_address")}</span>
        <AddressForm
          cityKey="address_city"
          className="gap-2"
          countryKey="country"
          errors={errors}
          inputClassName={clsxm(
            " text-neutral-black",
            !errors.address_city &&
              !errors.address_postal &&
              !errors.address_street &&
              !errors.country &&
              !errors.place_description &&
              !errors.place_id &&
              "border-neutral-grey-light-dark",
          )}
          placeDescriptionKey="place_description"
          placeIdKey="place_id"
          postalKey="address_postal"
          storeForm={company}
          storeSetter={setCompanyValue}
          streetKey="address_street"
          title={t("company_address")}
          titleClassName="text-xs text-neutral-black font-normal ml-1"
        />
      </div>
      <PhoneInput
        buttonClassName="border-neutral-grey-light-dark"
        className="placeholder:text-neutral-grey-light"
        containerClassName="flex flex-col gap-1"
        value={company.phonenumber ?? ""}
        onChange={value => setCompanyValue("phonenumber", value)}
        defaultCountry="CH"
        numberInputClassName={clsxm(" text-neutral-black", !errors.phonenumber && "border-neutral-grey-light-dark")}
        label={t("phone_number_label")}
        labelClassName="text-base text-neutral-black font-semibold"
        error={errors.phonenumber}
      />
      <div className="flex flex-col gap-2">
        <span className="text-neutral-black font-semibold">{t("company_socials_title")}</span>
        <div className="flex gap-5">
          <div className="flex w-full flex-col gap-2">
            <span className="text-neutral-black text-xs">
              {t("companies_platform_username", {platform: t("common:instagram")})}
            </span>
            <SocialField
              error={errors.username}
              placeholder={t("companies_platform_username", {platform: t("common:instagram")})}
              onChange={value => {
                setCompanyValue("username", value);
              }}
              setError={message => {
                setError("username", message ?? "");
              }}
              type="instagram"
              value={company.username}
            />
          </div>
          <div className="flex w-full flex-col gap-2">
            <span className="text-neutral-black text-xs">
              {t("companies_platform_username", {platform: t("common:tiktok")})}
            </span>
            <SocialField
              error={errors.tiktok_username}
              placeholder={t("companies_platform_username", {platform: t("common:tiktok")})}
              onChange={value => {
                setCompanyValue("tiktok_username", value);
              }}
              setError={message => {
                setError("tiktok_username", message ?? "");
              }}
              type="tiktok"
              value={company.tiktok_username}
            />
          </div>
        </div>
      </div>
      <Button
        buttonStyle="Gradient"
        className="w-fit self-end px-8 py-5"
        isDisabled={some(errors, v => !isEmpty(v))}
        isLoading={isCompanyUpdatePending}
        onClick={() => {
          if (validatePersonalInfo()) {
            handleCompanyUpdate();
          }
        }}>
        {t("common:Save")}
      </Button>
    </Modal>
  );
};

export default EditProfileModal;
